// BulkPayrollDialog.js
// Dialog for bulk payroll submission for multiple employees

import React, { useState, useEffect, useRef } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  Paper,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
} from '@mui/material';
import MobileDialog from './MobileDialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createBulkPayrollSubmissions } from '../../services/employeeService';

const months = [
  { value: 1, label: 'ינואר' },
  { value: 2, label: 'פברואר' },
  { value: 3, label: 'מרץ' },
  { value: 4, label: 'אפריל' },
  { value: 5, label: 'מאי' },
  { value: 6, label: 'יוני' },
  { value: 7, label: 'יולי' },
  { value: 8, label: 'אוגוסט' },
  { value: 9, label: 'ספטמבר' },
  { value: 10, label: 'אוקטובר' },
  { value: 11, label: 'נובמבר' },
  { value: 12, label: 'דצמבר' },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

const BulkPayrollDialog = ({ open, onClose, employees = [] }) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const errorRef = useRef(null);

  // Form state
  const [periodYear, setPeriodYear] = useState(currentYear);
  const [periodMonth, setPeriodMonth] = useState(new Date().getMonth() + 1);
  const [selectedEmployees, setSelectedEmployees] = useState({});
  const [employeeData, setEmployeeData] = useState({});

  // Initialize when dialog opens
  useEffect(() => {
    if (open && employees.length > 0) {
      const now = new Date();
      setPeriodYear(now.getFullYear());
      setPeriodMonth(now.getMonth() + 1);

      // Initialize selection and data for all active employees
      const initialSelected = {};
      const initialData = {};

      employees.forEach((emp) => {
        if (emp.Status === 'active') {
          initialSelected[emp.EmployeeID] = true;
          initialData[emp.EmployeeID] = {
            HoursWorked: '',
            Deductions: '',
            Additions: '',
          };
        }
      });

      setSelectedEmployees(initialSelected);
      setEmployeeData(initialData);
      setError('');
    }
  }, [open, employees]);

  // Scroll to error
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const createMutation = useMutation({
    mutationFn: createBulkPayrollSubmissions,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payroll-submissions'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['employeeMetrics'] });
      if (data.errors && data.errors.length > 0) {
        setError(`נוצרו ${data.created} משכורות בהצלחה. שגיאות: ${data.errors.join(', ')}`);
      } else {
        onClose();
      }
    },
    onError: (err) => {
      setError(err?.response?.data?.error || 'שגיאה בהגשת משכורות');
      setIsSubmitting(false);
    },
  });

  const handleToggleEmployee = (employeeId) => {
    setSelectedEmployees((prev) => ({
      ...prev,
      [employeeId]: !prev[employeeId],
    }));
  };

  const handleSelectAll = () => {
    const allSelected = employees.filter((e) => e.Status === 'active').every((e) => selectedEmployees[e.EmployeeID]);
    const newSelected = {};
    employees.forEach((emp) => {
      if (emp.Status === 'active') {
        newSelected[emp.EmployeeID] = !allSelected;
      }
    });
    setSelectedEmployees(newSelected);
  };

  const handleDataChange = (employeeId, field, value) => {
    setEmployeeData((prev) => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [field]: value,
      },
    }));
  };

  const calculateTotal = (employee) => {
    const data = employeeData[employee.EmployeeID] || {};
    let base = 0;

    if (employee.WageType === 'hourly' || employee.WageType === 'daily') {
      const qty = parseFloat(data.HoursWorked) || 0;
      base = qty * parseFloat(employee.WageAmount);
    } else {
      base = parseFloat(employee.WageAmount);
    }

    const deductions = parseFloat(data.Deductions) || 0;
    const additions = parseFloat(data.Additions) || 0;

    return base - deductions + additions;
  };

  const getGrandTotal = () => {
    return employees
      .filter((emp) => selectedEmployees[emp.EmployeeID])
      .reduce((sum, emp) => sum + calculateTotal(emp), 0);
  };

  const handleSubmit = async () => {
    setError('');

    const selectedList = employees.filter((emp) => selectedEmployees[emp.EmployeeID]);

    if (selectedList.length === 0) {
      setError('יש לבחור לפחות עובד אחד');
      return;
    }

    // Validate hourly/daily employees have hours/days
    for (const emp of selectedList) {
      if (emp.WageType === 'hourly' || emp.WageType === 'daily') {
        const qty = parseFloat(employeeData[emp.EmployeeID]?.HoursWorked) || 0;
        if (qty <= 0) {
          const label = emp.WageType === 'hourly' ? 'שעות עבודה' : 'ימי עבודה';
          setError(`יש להזין ${label} עבור ${emp.FirstName} ${emp.LastName}`);
          return;
        }
      }
    }

    setIsSubmitting(true);

    const submissions = selectedList.map((emp) => {
      const data = employeeData[emp.EmployeeID] || {};
      return {
        EmployeeID: emp.EmployeeID,
        HoursWorked: emp.WageType === 'hourly' ? parseFloat(data.HoursWorked) : null,
        Deductions: parseFloat(data.Deductions) || 0,
        Additions: parseFloat(data.Additions) || 0,
      };
    });

    createMutation.mutate({
      PeriodYear: parseInt(periodYear, 10),
      PeriodMonth: parseInt(periodMonth, 10),
      submissions,
    });
  };

  const activeEmployees = employees.filter((e) => e.Status === 'active');
  const selectedCount = Object.values(selectedEmployees).filter(Boolean).length;
  const allSelected = activeEmployees.length > 0 && activeEmployees.every((e) => selectedEmployees[e.EmployeeID]);

  return (
    <MobileDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>הגשת משכורות מרובה</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && (
              <Alert ref={errorRef} severity="error">{error}</Alert>
            )}

            {/* Period selection */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>שנה</InputLabel>
                <Select
                  value={periodYear}
                  onChange={(e) => setPeriodYear(e.target.value)}
                  label="שנה"
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>חודש</InputLabel>
                <Select
                  value={periodMonth}
                  onChange={(e) => setPeriodMonth(e.target.value)}
                  label="חודש"
                >
                  {months.map((month) => (
                    <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ flex: 1 }} />
              <Typography variant="h6" sx={{ alignSelf: 'center' }}>
                נבחרו: {selectedCount} עובדים
              </Typography>
            </Box>

            <Divider />

            {/* Employees table */}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selectedCount > 0 && !allSelected}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>שם העובד</TableCell>
                    <TableCell>סוג שכר</TableCell>
                    <TableCell>שכר בסיס</TableCell>
                    <TableCell>שעות/ימים</TableCell>
                    <TableCell>ניכויים</TableCell>
                    <TableCell>תוספות</TableCell>
                    <TableCell>סה"כ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeEmployees.map((emp) => {
                    const isSelected = selectedEmployees[emp.EmployeeID];
                    const data = employeeData[emp.EmployeeID] || {};
                    const total = calculateTotal(emp);

                    return (
                      <TableRow
                        key={emp.EmployeeID}
                        selected={isSelected}
                        sx={{ opacity: isSelected ? 1 : 0.5 }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleToggleEmployee(emp.EmployeeID)}
                          />
                        </TableCell>
                        <TableCell>{emp.FirstName} {emp.LastName}</TableCell>
                        <TableCell>{emp.WageType === 'hourly' ? 'שעתי' : emp.WageType === 'daily' ? 'יומי' : 'חודשי'}</TableCell>
                        <TableCell>₪{parseFloat(emp.WageAmount).toLocaleString()}</TableCell>
                        <TableCell>
                          {(emp.WageType === 'hourly' || emp.WageType === 'daily') ? (
                            <TextField
                              type="number"
                              size="small"
                              placeholder={emp.WageType === 'hourly' ? 'שעות' : 'ימים'}
                              value={data.HoursWorked || ''}
                              onChange={(e) => handleDataChange(emp.EmployeeID, 'HoursWorked', e.target.value)}
                              disabled={!isSelected}
                              sx={{ width: 80 }}
                              inputProps={{ min: 0, step: 0.5 }}
                            />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={data.Deductions || ''}
                            onChange={(e) => handleDataChange(emp.EmployeeID, 'Deductions', e.target.value)}
                            disabled={!isSelected}
                            sx={{ width: 80 }}
                            inputProps={{ min: 0 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={data.Additions || ''}
                            onChange={(e) => handleDataChange(emp.EmployeeID, 'Additions', e.target.value)}
                            disabled={!isSelected}
                            sx={{ width: 80 }}
                            inputProps={{ min: 0 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body1"
                            fontWeight={isSelected ? 'bold' : 'normal'}
                          >
                            ₪{total.toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {activeEmployees.length === 0 && (
              <Alert severity="info">אין עובדים פעילים להגשת משכורת</Alert>
            )}

            <Divider />

            {/* Grand total */}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <Typography variant="h6" align="center">
                סה"כ לתשלום: ₪{getGrandTotal().toLocaleString()}
              </Typography>
              <Typography variant="body1" align="center" sx={{ mt: 1 }}>
                ({selectedCount} עובדים × {months.find((m) => m.value === periodMonth)?.label} {periodYear})
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            ביטול
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting || selectedCount === 0}
          >
            {isSubmitting ? <CircularProgress size={24} /> : `הגש ${selectedCount} משכורות`}
          </Button>
      </DialogActions>
    </MobileDialog>
  );
};

export default BulkPayrollDialog;
